import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { toast } from '../lib/toast';
import { useSocket } from '../context/SocketContext';
import {
    ApiResponse, DatoAnalisis, ChatAnalistaMensaje, Cliente,
    ESTADO_DATO_LABELS, ESTADO_DATO_COLORS
} from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
// .xls (Excel binario viejo) se sacó: el backend migró de xlsx (SheetJS, con
// CVEs sin parche) a exceljs, que solo lee el formato moderno .xlsx/.xlsm.
// Ver uploadDatosAnalisis.ts en el backend.
const EXTENSIONES_ACEPTADAS = '.xlsx,.csv,.pdf';

const formatFecha = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatTamanio = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const PanelDatosAnalisis: React.FC<{ clientes: Cliente[] }> = ({ clientes }) => {
    const [clienteId, setClienteId] = useState<string>('');
    const [datos, setDatos] = useState<DatoAnalisis[]>([]);
    const [mensajes, setMensajes] = useState<ChatAnalistaMensaje[]>([]);
    const [subiendo, setSubiendo] = useState(false);
    const [pregunta, setPregunta] = useState('');
    const [enviando, setEnviando] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const { on, off } = useSocket();

    const fetchDatos = useCallback(async () => {
        if (!clienteId) { setDatos([]); return; }
        try {
            const res = await apiFetch(`${API_BASE}/analista/datos?cliente_id=${clienteId}`);
            const json: ApiResponse<DatoAnalisis[]> = await res.json();
            if (json.success && json.data) setDatos(json.data);
        } catch (e) {
            console.error(e);
        }
    }, [clienteId]);

    const fetchHistorial = useCallback(async () => {
        if (!clienteId) { setMensajes([]); return; }
        try {
            const res = await apiFetch(`${API_BASE}/analista/chat/historial?cliente_id=${clienteId}`);
            const json: ApiResponse<ChatAnalistaMensaje[]> = await res.json();
            if (json.success && json.data) setMensajes(json.data);
        } catch (e) {
            console.error(e);
        }
    }, [clienteId]);

    useEffect(() => { fetchDatos(); fetchHistorial(); }, [fetchDatos, fetchHistorial]);

    useEffect(() => {
        const refresh = () => fetchDatos();
        on('dato:procesado', refresh);
        return () => off('dato:procesado', refresh);
    }, [on, off, fetchDatos]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensajes]);

    const handleSubir = async (file: File) => {
        if (!clienteId) return;
        setSubiendo(true);
        try {
            const form = new FormData();
            form.append('cliente_id', clienteId);
            form.append('archivo', file);
            const res = await apiFetch(`${API_BASE}/analista/datos`, { method: 'POST', body: form });
            const json: ApiResponse<DatoAnalisis> = await res.json();
            if (json.success) {
                toast.success('Archivo recibido, procesando...');
                fetchDatos();
            } else {
                toast.error(json.error || 'Error al subir el archivo');
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        } finally {
            setSubiendo(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDescartarDato = async (id: number, version: number) => {
        try {
            const res = await apiFetch(`${API_BASE}/analista/datos/${id}/descartar`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version })
            });
            const json: ApiResponse<null> = await res.json();
            if (json.success) { toast.success('Archivo descartado'); fetchDatos(); }
            else toast.error(json.error === 'VERSION_MISMATCH' ? 'El registro cambió, recargá' : (json.error || 'Error'));
        } catch {
            toast.error('No se pudo conectar con el servidor');
        }
    };

    const handleEnviarPregunta = async () => {
        if (!pregunta.trim() || !clienteId) return;
        const texto = pregunta.trim();
        setPregunta('');
        setEnviando(true);
        // Optimista: se muestra la pregunta del usuario ya mismo, sin esperar la respuesta
        setMensajes(prev => [...prev, { id: -1, cliente_id: Number(clienteId), rol: 'user', contenido: texto, creado_en: new Date().toISOString() }]);
        try {
            const res = await apiFetch(`${API_BASE}/analista/chat/mensaje`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cliente_id: clienteId, mensaje: texto })
            });
            const json: ApiResponse<{ respuesta: string }> = await res.json();
            if (json.success) {
                fetchHistorial();
            } else {
                toast.error(json.error || 'Error al consultar la IA');
                fetchHistorial(); // saca el mensaje optimista y muestra el estado real (el mensaje del usuario sí se guardó)
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-slate-800">Analista de Datos</h2>
                <p className="text-sm text-slate-500">Subí planillas de ventas/cobranzas de un cliente y preguntale a la IA sobre esos datos.</p>
            </div>

            <select
                value={clienteId}
                onChange={e => setClienteId(e.target.value)}
                className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
            >
                <option value="">Elegí un cliente...</option>
                {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.empresa}</option>
                ))}
            </select>

            {!clienteId && (
                <p className="text-sm text-slate-500 text-center py-8">Elegí un cliente para ver sus datos y chatear con la IA.</p>
            )}

            {clienteId && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1 space-y-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-700">Archivos</h3>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={subiendo}
                                className="text-xs px-2.5 py-1 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
                            >
                                {subiendo ? 'Subiendo...' : '+ Subir'}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={EXTENSIONES_ACEPTADAS}
                                className="hidden"
                                onChange={e => { const f = e.target.files?.[0]; if (f) handleSubir(f); }}
                            />
                        </div>
                        <p className="text-xs text-slate-500">XLSX, CSV o PDF con texto (no escaneado) — máx 20MB. (XLS viejo no: re-guardalo como XLSX)</p>

                        {datos.length === 0 && <p className="text-xs text-slate-500 py-4">Sin archivos todavía.</p>}
                        {datos.map(d => (
                            <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-2.5 text-xs">
                                <div className="flex items-start justify-between gap-2">
                                    <span className="font-medium text-slate-700 break-all">{d.nombre_original}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full whitespace-nowrap ${ESTADO_DATO_COLORS[d.estado]}`}>
                                        {ESTADO_DATO_LABELS[d.estado]}
                                    </span>
                                </div>
                                <div className="text-slate-500 mt-1">{formatTamanio(d.tamanio_bytes)} · {formatFecha(d.creado_en)}</div>
                                {d.estado === 'error' && <div className="text-red-600 mt-1">{d.error_detalle}</div>}
                                <button onClick={() => handleDescartarDato(d.id, d.version)} className="text-slate-500 hover:text-red-600 mt-1">
                                    Descartar
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="lg:col-span-2 flex flex-col bg-white border border-slate-200 rounded-xl h-[520px]">
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {mensajes.length === 0 && (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    Preguntale algo a la IA sobre los datos de este cliente.
                                </p>
                            )}
                            {mensajes.map(m => (
                                <div key={m.id} className={`flex ${m.rol === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                                        m.rol === 'user' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-800'
                                    }`}>
                                        {m.contenido}
                                    </div>
                                </div>
                            ))}
                            {enviando && (
                                <div className="flex justify-start">
                                    <div className="bg-slate-100 text-slate-500 rounded-xl px-3 py-2 text-sm">Pensando...</div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="border-t border-slate-100 p-3 flex gap-2">
                            <input
                                type="text"
                                value={pregunta}
                                onChange={e => setPregunta(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !enviando) handleEnviarPregunta(); }}
                                placeholder="Preguntá algo sobre las ventas/cobranzas de este cliente..."
                                className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2"
                                disabled={enviando}
                            />
                            <button
                                onClick={handleEnviarPregunta}
                                disabled={enviando || !pregunta.trim()}
                                className="px-3 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
                            >
                                Enviar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
